<template>
  <div>
  <!-- add sms validation here -->
   <LockLoader v-if="fetching" text="Carregando" />
   <div v-show="showCodeOptions && !fetching && !showSecurityCode">

    <h1 class="title">Valide seu celular</h1>
    <p class="description">Escolha como quer receber o código de validação.</p>

      <div class="feedback-card" v-if="currentMethod && requestError">
        <div class="error-message">
          <ExclamationIcon class="error-icon" />
          <p>{{ `O envio por ${getOptionLabel(currentMethod)}
           está indisponível no momento` }}</p>
        </div>
      </div>

      <section  class="options">
        <button
        v-for="option in options"
        :key="option.method"
        class="option"
        :class="{ disabled: isDisabledMethod(option) }"
        @click="isDisabledMethod(option) ? null : chooseMethod(option.method)"
      >
        <div class="option-content">
          <div class="icon-wrapper">
            <component
              :is="option.icon"
              :class="{
                'whatsapp-icon': option.method === 'WHATSAPP',
                'text-gray': isDisabledMethod(option),
                'text-dark': !isDisabledMethod(option),
              }"
              class="option-icon"
            />
          </div>
          <div class="option-text">
            <span class="option-title" :class="{ 'text-gray': isDisabledMethod(option) }">
              Receber por {{ getOptionLabel(option.method) }}
            </span>
            <span class="option-subtitle" :class="{ 'text-gray': isDisabledMethod(option) }">
              <ExclamationIcon v-if="isDisabledMethod(option)" class="warning-icon" />
              {{ isDisabledMethod(option) ? 'Serviço indisponível' : option.value }}
            </span>
          </div>
          <ChevronRightIcon class="chevron-icon"
          :class="{ 'text-gray': isDisabledMethod(option) }" />
        </div>
      </button>
      </section>
   </div>
   <div v-if="showSecurityCode">
    <h1 class="title">Código de validação</h1>
        <p>
          {{
            `Digite o código enviado para o número cadastrado.`
          }}
        </p>
        <div style="margin-top: 24px;">
          <input-token
            ref="input-token"
            :value="token"
            :length="6"
            :style="{ 'pointer-events': confirmCodeError ? 'none' : 'auto' }"
            :status="verifyStatus"
            @complete="sendSecurityCode"
            @backspace="handleBackspace"
          />
        </div>
        <div v-if="confirmCodeError" class="code-error-message">
          <p >Código incorreto, tente novamente</p>
        </div>
        <div class="resend-text" v-if="!['loading', 'success'].includes(verifyStatus)">
        <a @click="handleShowResendOptions"
          :class="{ 'disabled': resendCounter != 0 }"
        >
          Não recebi o código {{ resendCounter !== 0 ? `(${resendCounter}s)` : '' }}
        </a>
      </div>
   </div>
  <div v-show="!showCodeOptions && !showSecurityCode"
  id="checkout-info" :class="{ mobile: isMobile() }">
    <div v-if="hasInvalidInputs">
      <AppAlert
        :input="inputErrors"
        :className="'app-alert-error'"
        :class="'app-alert-error'"
        colorType="danger"
        :getTextFromOptions="translateErrorText"
        :closeButton="false"
      />
    </div>
    <div class="checkout-info-title">
      Pague em até <span class="body-bold">{{simulation.numberOfInstallments}} vezes</span>
      de <span class="body-bold">{{simulation.pmt | moneyFilter}}</span> no
      <span class="body-bold">Pix ou Boleto</span>
    </div>
    <div
      class="checkout-info-subtitle">
      Valor total de {{ simulation.totalValue | moneyFilter }}
    </div>
    <div class="checkout-info-body">
      <CheckoutDescriptionSteps />
    </div>
    <div class="checkout-info-footer">
      <div class="checkout-info-disclaimer">
        Estou de acordo com o
        <a target="_blank"
          href="https://www.parcelex.com.br/termos-de-uso">
          Termo de uso
        </a>
        e
        <a
          target="_blank"
          href="https://www.parcelex.com.br/politica-de-privacidade">
          Política de Privacidade
        </a>
        do Parcelex.
      </div>

      <Button
        v-if="userNeedAuthenticate"
        class="primary"
        @click="emitNeedAuthenticateEvent"
      >
        Autenticar acesso na loja
      </Button>

      <VerifyButton
        v-else
        @verify="creditVerify"
        @completed="onBtnVerifyFilled"
        :disabled="hasInvalidInputs"
        ref="verify-btn"
        class="verify-button"
        :width="isMobile () ? '100%' : '300px'"
      />
    </div>

    <p class="x-small-regular gray-500">
      Esta é uma simulação com juros e CET de {{ simulation.interest | interestRateFilter }}
      ao mês. O valor total pode variar de acordo com a taxa de juros, que é definida a partir de
      seu perfil de crédito.
    </p>
  </div>
</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import VerifyButton from '@/components/common/VerifyButton.vue';
import StringMixin from '@/mixins/string';
import UtilMixin from '@/mixins/util';
import { DeviceMobileIcon, ExclamationIcon, ChevronRightIcon } from '@vue-hero-icons/outline';
import LockLoader from '@/components/common/LockLoader.vue';
import InputToken from '@/components/common/InputToken.vue';
import AppAlert from '../components/AppAlert.vue';
import CheckoutDescriptionSteps from '../components/CheckoutDescriptionSteps.vue';
import WhatsappIcon from '../components/WhatsappIcon.vue';

export default {
  name: 'checkout-info',
  data: () => ({
    translateErrorText: (inputErrors) => {
      const textOptions = {
        needAuthenticate: 'Você deve se autenticar na loja para verificar seu crédito.',
        phone: 'Telefone',
        ddd: 'DDD',
        email: 'Email',
        cpf: 'CPF',
        shipping: 'CEP',
        genericError: 'Você deve preencher apenas dados válidos.',
      };
      if (Array.isArray(inputErrors)) {
        if (inputErrors.length === 1 || inputErrors[0] === 'needAuthenticate') {
          return textOptions[inputErrors[0]] || '';
        }
        if (inputErrors[0] === 'phone' && inputErrors[1] === 'ddd' && inputErrors.length === 2) {
          return textOptions.ddd;
        }
        if (inputErrors.length > 1) {
          return textOptions.genericError;
        }
      }
      return '';
    },
    showCodeOptions: false,
    showSecurityCode: false,
    confirmCodeError: false,
    fetching: false,
    requestErrors: [],
    verifyStatus: null,
    resendCounter: 90,
    token: '',
  }),
  computed: {
    ...mapGetters('checkout', [
      'simulation',
      'isApproved',
    ]),
    ...mapState('checkout', [
      'inputErrors',
      'currentMethod',
      'user',
      'apiKey',
    ]),
    options() {
      return [
        {
          method: 'SMS',
          icon: DeviceMobileIcon,
          value: this.hidePhone(this.user.phone),
          active: process.env.VUE_APP_DISABLE_SMS !== 'true',
        },
        {
          method: 'WHATSAPP',
          icon: WhatsappIcon,
          value: this.hidePhone(this.user.phone),
          active: process.env.VUE_APP_DISABLE_WHATSAPP !== 'true',
        },
      ];
    },
    hasInvalidInputs() {
      return Array.isArray(this.inputErrors) && this.inputErrors.length >= 1;
    },
    userNeedAuthenticate() {
      return Array.isArray(this.inputErrors)
        && this.inputErrors.length >= 1
        && this.inputErrors.find((el) => el === 'needAuthenticate');
    },
    requestError() {
      return this.requestErrors.length > 0;
    },
  },
  mixins: [StringMixin, UtilMixin],
  methods: {
    ...mapActions('checkout', [
      'getOptions',
      'goToPage',
      'emitEvent',
      'needAuthenticate',
      'sendConfirmationCode',
      'confirmSecurityCode',
    ]),
    ...mapMutations('checkout', [
      'setCurrentMethod',
    ]),
    hidePhone(phone) {
      if (phone === '') return phone;

      const cleanPhoneString = phone.replace(/[^\d]/g, '');
      const formatedPhone = cleanPhoneString.replace(/(\d{2})(\d{9})/, '($1) $2');

      return `${formatedPhone.substring(0, 10)} *****`;
    },
    isNumberNotNaN(val) {
      return typeof val === 'number' && val >= 0;
    },
    async creditVerify() {
      const confirmationRetailers = ['62fab071084fd00ea7d1cf86', '6478beb10468fb0af4d71e25'];
      if (confirmationRetailers.includes(this.apiKey)) {
        this.showCodeOptions = true;
      } else {
        await this.fetchOptions();
      }
    },
    async fetchOptions() {
      try {
        await this.getOptions();
        this.$refs['verify-btn'].complete(this.isApproved);
      } catch (error) {
        this.$emit('onError', error);
      }
    },
    onBtnVerifyFilled() {
      if (this.isApproved) {
        this.goToPage('checkout-feedback-approved');
      } else {
        this.goToPage('checkout-denied');
      }
      setTimeout(() => {
        this.showSecurityCode = false;
      }, 2000);
    },
    emitNeedAuthenticateEvent() {
      this.needAuthenticate();
    },
    getOptionLabel(method) {
      switch (method) {
        case 'WHATSAPP':
          return 'Whatsapp';
        case 'SMS':
          return 'SMS';
        default:
          return '';
      }
    },
    async chooseMethod(method) {
      this.fetching = true;
      try {
        await this.sendConfirmationCode(method);
        this.showCodeOptions = false;
        this.showSecurityCode = true;
      } catch (error) {
        this.requestErrors.push(method);
        this.showCodeOptions = true;
      } finally {
        this.fetching = false;
        this.resendCounter = 90;
        this.startResendCounter();
      }
    },
    async sendSecurityCode(token) {
      this.verifyStatus = 'loading';
      try {
        await this.confirmSecurityCode(token);
        this.verifyStatus = 'success';
        await this.fetchOptions();
      } catch (error) {
        this.confirmCodeError = true;
        setTimeout(() => {
          this.confirmCodeError = false;
          this.$refs['input-token'].cleanToken();
        }, 2000);
        this.verifyStatus = null;
      } finally {
        this.fetching = false;
      }
    },
    isDisabledMethod(option) {
      return this.requestErrors.includes(option.method) || !option.active;
    },
    handleBackspace() {
      this.verifyStatus = null;
    },
    handleShowResendOptions() {
      this.showCodeOptions = true;
      this.showSecurityCode = false;
      this.resendConfirmationCode();
    },
    resendConfirmationCode() {
      if (this.resendCounter !== 0) return;
      this.resendCounter = 90;
      this.startResendCounter();
    },
    startResendCounter() {
      const resendInterval = setInterval(() => {
        this.resendCounter -= 1;
        if (this.resendCounter === 0) {
          clearInterval(resendInterval);
        }
      }, 1000);
    },
  },
  mounted() {
    this.emitEvent({
      type: 'application',
    });
    this.setCurrentMethod(null);
  },
  components: {
    VerifyButton,
    AppAlert,
    CheckoutDescriptionSteps,
    ChevronRightIcon,
    ExclamationIcon,
    DeviceMobileIcon,
    WhatsappIcon,
    LockLoader,
    InputToken,
  },
};
</script>

<style lang="scss" scoped>
@import '../../../scss/styles/colors.scss';
.body-regular-v2 {
  font-size: 16px;
  line-height: 24px;
}

.small-regular-v2 {
  font-size: 14px;
  line-height: 20px;
}

#checkout-info {
  .checkout-info-title {
    @extend .body-regular-v2;
    margin-bottom: 4px;
  }

  .checkout-info-subtitle {
    @extend .small-regular-v2;
    color: var(--gray-500);
  }

  .checkout-info-footer {
    display: flex;
    justify-content: center;
    align-items: center;

    gap: 20px;

    margin-bottom: 66px;
  }

  .checkout-info-disclaimer {
    @extend .small-regular-v2;
  }

  &.mobile {
    .checkout-info-title, .checkout-info-title .body-bold {
      font-size: 20px;
      line-height: 24px;
    }

    .checkout-info-title {
      margin-bottom: 8px
    }

    .checkout-info-subtitle {
      @extend .body-regular-v2;
    }

    .checkout-info-footer {
      flex-direction: column-reverse;
      gap: 16px;

      margin-bottom: 41px;
    }

    .checkout-info-disclaimer {
      @extend .body-regular-v2;
    }
  }
}

.checkout-info-body {
  @extend .small-regular;
  margin-bottom: 20px;
}

.error-card {
  margin-bottom: 15px;
}

.feedback-card {
  display: flex;
  padding: 0.75rem;
  gap: 0.5rem;
  border-radius: 0.75rem;
  width: 95%;
  color: #B91C1C;
  background-color: #FEE2E2;

  .error-message {
    display: flex;
    align-items: center;
    color: #d9534f;
    font-weight: bold;

    .error-icon {
      margin-right: 8px;
      width: 20px;
      height: 20px;
    }
  }
}

$gray: #d1d5db;
$dark-gray: #6b7280;
$light-gray: #f5f5f5;
$border-color: #ccc;
$warning-color: #d9534f;
$primary-bg: #ffffff;

.container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.logo {
  margin-bottom: 20px;
  height: 36px;
  align-self: flex-start;
}

.description {
  margin-top: 12px;
  margin-bottom: 15px;
}

.feedback-card {
  margin-bottom: 15px;
}

.options {
  display: flex;
  flex-direction: column;
}

.option {
  width: 100%;
  border-bottom: 1px solid $border-color;
  padding: 15px;
  background-color: $primary-bg;
  transition: background 0.3s ease-in-out;
  cursor: pointer;
  display: flex;
  border: none;
  height: 80px;

  &:hover {
    background-color: $light-gray;
    border: none;
  }

  &.disabled {
    cursor: default;
    background-color: $light-gray;
    opacity: 0.5;
  }

  .option-content {
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%;
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: $gray;
  }

  .option-icon {
    width: 24px;
    height: 24px;
  }

  .whatsapp-icon {
    fill: currentColor;
  }

  .option-text {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .option-title {
    color: black;
    line-height: 24px;
    font-weight: bold;
  }

  .option-subtitle {
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .text-gray {
    color: $dark-gray;
  }

  .text-dark {
    color: #333;
  }

  .warning-icon {
    width: 16px;
    height: 16px;
    color: $dark-gray;
  }

  .chevron-icon {
    margin-left: auto;
    width: 24px;
    height: 24px;
  }
}

.back-link {
  position: absolute;
  bottom: 15px;
  display: flex;
  align-self: center;
  color: #000;
  gap: 5px;
}

.back-icon {
  width: 20px;
  height: 20px;
}

.title {
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
}

.resend-text {
    display: flex;
    justify-content: center;
    margin-top: 24px;

    a {
      @extend .body-regular;
      font-size: 16px;
      line-height: 24px;
      cursor: pointer;
      color: $primary-500;

      &:hover {
        color: $primary-600;
      }

      &.disabled {
        pointer-events: none;
        color: $gray-400;
      }
    }
  }

  .code-error-message {
    display: flex;
    justify-content: center;
    margin-top: 24px;

    p {
      color: #d9534f;
  }
}

</style>
